import { useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, ArrowRight, ChevronDown, Send, Mail, ArrowUpRight,
  BrainCircuit, LayoutTemplate, Smartphone, Search, PenTool, 
  Code2, Rocket, Shield, Zap, Users, FileCode 
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import axios from "axios";
import "./App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Navigation Links
const navLinks = [
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Technology", href: "#tech" },
  { label: "Why Us", href: "#why-us" },
  { label: "Contact", href: "#contact" },
];

// Services Data
const services = [
  {
    icon: BrainCircuit,
    title: "AI Farm",
    description: "Custom AI agents for support, operations, analytics, and internal tools. Document Q&A using company data. Automation of repetitive workflows with audit logs and controlled execution.",
    features: ["LLM Agents", "RAG Pipelines", "Workflow Automation", "Audit Logging"],
    span: "md:col-span-2 md:row-span-2",
  },
  {
    icon: LayoutTemplate,
    title: "Web Applications",
    description: "High-performance React and TypeScript frontends. Admin dashboards, internal tools, and SaaS products built for scale.",
    features: ["React + TypeScript", "Admin Dashboards", "SaaS Platforms"],
    span: "md:col-span-1 md:row-span-1",
  },
  {
    icon: Smartphone,
    title: "Mobile Engineering",
    description: "Cross-platform mobile applications with native-feel performance. Full backend integration and optimized UX.",
    features: ["Cross-Platform", "Backend Integration", "Performance UX"],
    span: "md:col-span-1 md:row-span-1",
  },
];

// Process Steps
const steps = [
  { icon: Search, title: "Discovery", description: "We understand your business, goals, and technical requirements through focused conversations.", number: "01" },
  { icon: PenTool, title: "Design", description: "Architecture planning and system design. Clear specifications before any code is written.", number: "02" },
  { icon: Code2, title: "Build", description: "Iterative development with regular check-ins. Clean, tested, maintainable code at every step.", number: "03" },
  { icon: Rocket, title: "Launch", description: "Deployment, monitoring setup, and handoff. Documentation and support included.", number: "04" },
];

// Technologies
const technologies = [
  { name: "React", category: "Frontend" },
  { name: "TypeScript", category: "Language" },
  { name: "Node.js", category: "Backend" },
  { name: "Python", category: "AI/ML" },
  { name: "MongoDB", category: "Database" },
  { name: "PostgreSQL", category: "Database" },
  { name: "AWS", category: "Cloud" },
  { name: "Docker", category: "DevOps" },
];

// Why Choose Us Reasons
const reasons = [
  { icon: FileCode, title: "Clean Architecture", description: "Code that's readable, testable, and maintainable. No shortcuts, no technical debt." },
  { icon: Shield, title: "Type Safety", description: "TypeScript everywhere. Catch bugs at compile time, not in production." },
  { icon: Zap, title: "Performance First", description: "Optimized from the start. Fast load times, efficient queries, responsive interfaces." },
  { icon: Users, title: "Clear Communication", description: "Regular updates, honest timelines, and no surprises. We speak plain English, not jargon." },
];

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const scrollToSection = (href) => {
    setIsOpen(false);
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-md border-b border-[#27272A]/50">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a href="/" className="font-heading font-bold text-lg md:text-xl tracking-tight" data-testid="logo">
            <span className="text-[#00E599]">COOKIE</span>PHANTOM
          </a>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button key={link.href} onClick={() => scrollToSection(link.href)} className="text-sm text-[#A1A1AA] hover:text-white transition-colors duration-200" data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}>
                {link.label}
              </button>
            ))}
            <Button onClick={() => scrollToSection("#contact")} className="bg-[#00E599] text-black hover:bg-[#00E599]/90 h-10 px-6 rounded-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98]" data-testid="nav-cta-button">
              Book a Consultation
            </Button>
          </div>
          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-button">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="md:hidden bg-[#0F0F0F] border-b border-[#27272A]">
            <div className="px-6 py-4 space-y-4">
              {navLinks.map((link) => (
                <button key={link.href} onClick={() => scrollToSection(link.href)} className="block w-full text-left text-[#A1A1AA] hover:text-white transition-colors" data-testid={`mobile-nav-${link.label.toLowerCase().replace(' ', '-')}`}>
                  {link.label}
                </button>
              ))}
              <Button onClick={() => scrollToSection("#contact")} className="w-full bg-[#00E599] text-black hover:bg-[#00E599]/90" data-testid="mobile-nav-cta-button">
                Book a Consultation
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Hero Component
const Hero = () => {
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      <div className="absolute inset-0 hero-glow pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-24 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="space-y-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <span className="inline-block px-4 py-1.5 bg-[#1F1F22] border border-[#27272A] rounded-full text-xs font-mono tracking-wider uppercase text-[#A1A1AA]">
                AI Farm • Web • Mobile
              </span>
            </motion.div>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.1]" data-testid="hero-headline">
              Engineering Intelligence<span className="block text-[#00E599]">for the Real World</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="text-base md:text-lg leading-relaxed text-[#A1A1AA] max-w-xl" data-testid="hero-subheadline">
              We build practical AI agents, scalable web systems, and mobile applications that drive business logic. Clean architecture, type safety, and code that lasts.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="flex flex-col sm:flex-row gap-4">
              <Button onClick={() => scrollToSection("#contact")} className="bg-[#00E599] text-black hover:bg-[#00E599]/90 h-12 px-8 rounded-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] group" data-testid="hero-cta-primary">
                Book a Consultation<ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button onClick={() => scrollToSection("#services")} variant="outline" className="bg-[#1F1F22] text-white hover:bg-[#1F1F22]/80 h-12 px-8 rounded-lg border border-[#27272A]/50 font-medium" data-testid="hero-cta-secondary">
                View Services
              </Button>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.6, duration: 0.8 }} className="relative hidden lg:block">
            <div className="relative rounded-2xl overflow-hidden border border-[#27272A]/40">
              <img src="https://images.pexels.com/photos/18545023/pexels-photo-18545023.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Abstract digital network representing AI infrastructure" className="w-full h-[500px] object-cover opacity-70" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-[#0F0F0F] border border-[#27272A]/40 rounded-xl p-4">
              <pre className="text-xs font-mono text-[#A1A1AA]">
                <code>
                  <span className="text-[#00E599]">const</span> stack = {'{\n'}
                  {"  "}frontend: <span className="text-[#00E599]">"React + TypeScript"</span>,{'\n'}
                  {"  "}backend: <span className="text-[#00E599]">"Node.js + TypeScript"</span>,{'\n'}
                  {"  "}ai: <span className="text-[#00E599]">"Custom Agents"</span>{'\n'}
                  {'}'};
                </code>
              </pre>
            </div>
          </motion.div>
        </div>
        <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} onClick={() => scrollToSection("#services")} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#A1A1AA] hover:text-white transition-colors animate-bounce" data-testid="scroll-down-button">
          <ChevronDown className="w-6 h-6" />
        </motion.button>
      </div>
    </section>
  );
};

// Services Component
const Services = () => (
  <section id="services" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
        <span className="text-xs font-mono tracking-wider uppercase text-[#A1A1AA]/80 mb-4 block">What We Build</span>
        <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight" data-testid="services-title">
          Practical Solutions, <span className="text-[#00E599]">Real Results</span>
        </h2>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[280px]">
        {services.map((service, index) => (
          <motion.div key={service.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className={`${service.span} bg-[#0F0F0F] border border-[#27272A]/40 rounded-xl p-8 hover:border-[#00E599]/50 transition-all duration-300 group relative overflow-hidden flex flex-col justify-between`} data-testid={`service-card-${service.title.toLowerCase().replace(' ', '-')}`}>
            <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 card-hover-gradient" />
            <div className="relative z-10">
              <div className="w-12 h-12 rounded-lg bg-[#1F1F22] flex items-center justify-center mb-6 group-hover:bg-[#00E599]/10 transition-colors">
                <service.icon className="w-6 h-6 text-[#00E599]" />
              </div>
              <h3 className="font-heading text-xl md:text-2xl font-medium mb-3">{service.title}</h3>
              <p className="text-[#A1A1AA] text-sm leading-relaxed">{service.description}</p>
            </div>
            <div className="relative z-10 flex flex-wrap gap-2 mt-4">
              {service.features.map((feature) => (
                <span key={feature} className="text-xs px-3 py-1 bg-[#1F1F22] rounded-full text-[#A1A1AA] border border-[#27272A]/50">{feature}</span>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// Process Component
const Process = () => (
  <section id="process" className="py-24 md:py-32 bg-[#0A0A0A]">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
        <span className="text-xs font-mono tracking-wider uppercase text-[#A1A1AA]/80 mb-4 block">How We Work</span>
        <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight" data-testid="process-title">
          A Clear Path from <span className="text-[#00E599]">Idea to Delivery</span>
        </h2>
      </motion.div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
        <div className="hidden md:block absolute top-16 left-[12%] right-[12%] h-px bg-gradient-to-r from-transparent via-[#27272A] to-transparent" />
        {steps.map((step, index) => (
          <motion.div key={step.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.15 }} className="relative text-center md:text-left" data-testid={`process-step-${index + 1}`}>
            <div className="flex flex-col items-center md:items-start">
              <div className="relative mb-6">
                <div className="w-14 h-14 rounded-xl bg-[#0F0F0F] border border-[#27272A]/40 flex items-center justify-center group hover:border-[#00E599]/50 transition-colors">
                  <step.icon className="w-6 h-6 text-[#00E599]" />
                </div>
                <span className="absolute -top-2 -right-2 text-xs font-mono text-[#00E599] bg-[#0A0A0A] px-1">{step.number}</span>
              </div>
              <h3 className="font-heading text-lg font-medium mb-2">{step.title}</h3>
              <p className="text-sm text-[#A1A1AA] leading-relaxed max-w-xs">{step.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

// TechStack Component
const TechStack = () => (
  <section id="tech" className="py-24 md:py-32">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
        <span className="text-xs font-mono tracking-wider uppercase text-[#A1A1AA]/80 mb-4 block">Technology</span>
        <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight" data-testid="tech-title">
          Built with <span className="text-[#00E599]">Modern Tools</span>
        </h2>
      </motion.div>
      <div className="grid md:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-[#0F0F0F] border border-[#27272A]/40 rounded-xl p-6 overflow-hidden">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-[#FF5F56]" />
            <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" />
            <div className="w-3 h-3 rounded-full bg-[#27C93F]" />
            <span className="ml-4 text-xs font-mono text-[#A1A1AA]">stack.config.ts</span>
          </div>
          <pre className="text-sm font-mono overflow-x-auto">
            <code>
              <span className="text-[#A1A1AA]">{"// CookiePhantom Stack Configuration"}</span>{"\n\n"}
              <span className="text-[#00E599]">export const</span> <span className="text-white">config</span> = {"{\n"}
              {"  "}<span className="text-[#A1A1AA]">frontend:</span> {"{\n"}
              {"    "}<span className="text-[#A1A1AA]">framework:</span> <span className="text-[#00E599]">"React 18+"</span>,{"\n"}
              {"    "}<span className="text-[#A1A1AA]">language:</span> <span className="text-[#00E599]">"TypeScript 5.x"</span>,{"\n"}
              {"    "}<span className="text-[#A1A1AA]">styling:</span> <span className="text-[#00E599]">"TailwindCSS"</span>,{"\n"}
              {"  }"},\n"}
              {"  "}<span className="text-[#A1A1AA]">backend:</span> {"{\n"}
              {"    "}<span className="text-[#A1A1AA]">runtime:</span> <span className="text-[#00E599]">"Node.js 20+"</span>,{"\n"}
              {"    "}<span className="text-[#A1A1AA]">language:</span> <span className="text-[#00E599]">"TypeScript"</span>,{"\n"}
              {"    "}<span className="text-[#A1A1AA]">ai:</span> <span className="text-[#00E599]">"Python + LangChain"</span>,{"\n"}
              {"  }"},\n"}
              {"  "}<span className="text-[#A1A1AA]">infrastructure:</span> {"{\n"}
              {"    "}<span className="text-[#A1A1AA]">cloud:</span> <span className="text-[#00E599]">"AWS / GCP"</span>,{"\n"}
              {"    "}<span className="text-[#A1A1AA]">containers:</span> <span className="text-[#00E599]">"Docker + K8s"</span>,{"\n"}
              {"    "}<span className="text-[#A1A1AA]">ci_cd:</span> <span className="text-[#00E599]">"GitHub Actions"</span>,{"\n"}
              {"  }}\n"}
              {"}"};
            </code>
          </pre>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-4">
          <div className="flex flex-wrap gap-4 justify-center md:justify-start">
            {technologies.map((tech, index) => (
              <motion.div key={tech.name} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} whileHover={{ scale: 1.05 }} className="bg-[#0F0F0F] border border-[#27272A]/40 rounded-lg px-6 py-4 hover:border-[#00E599]/50 transition-all cursor-default" data-testid={`tech-${tech.name.toLowerCase()}`}>
                <span className="font-medium text-white">{tech.name}</span>
                <span className="block text-xs text-[#A1A1AA] mt-1">{tech.category}</span>
              </motion.div>
            ))}
          </div>
          <p className="text-[#A1A1AA] text-sm leading-relaxed mt-8 text-center md:text-left">
            We choose tools based on project requirements, not trends. Every technology decision is made with long-term maintainability in mind.
          </p>
        </motion.div>
      </div>
    </div>
  </section>
);

// WhyChooseUs Component
const WhyChooseUs = () => (
  <section id="why-us" className="py-24 md:py-32 bg-[#0A0A0A]">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <div className="grid lg:grid-cols-2 gap-16 items-center">
        <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
          <span className="text-xs font-mono tracking-wider uppercase text-[#A1A1AA]/80 mb-4 block">Why Choose Us</span>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight mb-6" data-testid="why-us-title">
            Engineering You Can <span className="text-[#00E599]">Trust</span>
          </h2>
          <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed mb-8">
            We're not a marketing agency that writes code. We're engineers who build systems that work. Our focus is on delivering real value, not impressive demos that fall apart in production.
          </p>
          <div className="relative rounded-xl overflow-hidden">
            <img src="https://images.pexels.com/photos/8837370/pexels-photo-8837370.jpeg?auto=compress&cs=tinysrgb&w=800" alt="Engineering team collaborating" className="w-full h-64 object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A] via-transparent to-transparent" />
          </div>
        </motion.div>
        <div className="space-y-6">
          {reasons.map((reason, index) => (
            <motion.div key={reason.title} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="flex gap-4 p-4 bg-[#0F0F0F] border border-[#27272A]/40 rounded-xl hover:border-[#00E599]/30 transition-colors" data-testid={`reason-${index + 1}`}>
              <div className="w-10 h-10 rounded-lg bg-[#1F1F22] flex items-center justify-center shrink-0">
                <reason.icon className="w-5 h-5 text-[#00E599]" />
              </div>
              <div>
                <h3 className="font-heading font-medium mb-1">{reason.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{reason.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Contact Component
const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <span className="text-xs font-mono tracking-wider uppercase text-[#A1A1AA]/80 mb-4 block">Get in Touch</span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight mb-6" data-testid="contact-title">
              Let's Build<span className="block text-[#00E599]">Something Real</span>
            </h2>
            <p className="text-[#A1A1AA] text-base md:text-lg leading-relaxed mb-8">
              Have a project in mind? We'd like to hear about it. Tell us what you're building, and we'll get back to you within 24 hours.
            </p>
            <div className="space-y-4">
              <a href="mailto:hello@cookiephantom.ai" className="flex items-center gap-3 text-[#A1A1AA] hover:text-white transition-colors group" data-testid="contact-email">
                <Mail className="w-5 h-5 text-[#00E599]" /><span>hello@cookiephantom.ai</span><ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            </div>
            <div className="mt-12 p-6 bg-[#0F0F0F] border border-[#27272A]/40 rounded-xl">
              <h3 className="font-heading font-medium mb-2">What happens next?</h3>
              <ol className="text-sm text-[#A1A1AA] space-y-2">
                <li>1. We review your message and project details</li>
                <li>2. Schedule a 30-minute discovery call</li>
                <li>3. Provide a clear scope and estimate</li>
                <li>4. Begin work if we're a good fit</li>
              </ol>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2">Name <span className="text-[#00E599]">*</span></label>
                  <Input id="name" name="name" type="text" value={formData.name} onChange={handleChange} placeholder="Your name" className="bg-[#1F1F22]/50 border-[#27272A]/50 focus:border-[#00E599] focus:ring-1 focus:ring-[#00E599]/50 h-12 rounded-lg" data-testid="contact-name-input" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">Email <span className="text-[#00E599]">*</span></label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="you@company.com" className="bg-[#1F1F22]/50 border-[#27272A]/50 focus:border-[#00E599] focus:ring-1 focus:ring-[#00E599]/50 h-12 rounded-lg" data-testid="contact-email-input" />
                </div>
              </div>
              <div>
                <label htmlFor="company" className="block text-sm font-medium mb-2">Company</label>
                <Input id="company" name="company" type="text" value={formData.company} onChange={handleChange} placeholder="Your company name (optional)" className="bg-[#1F1F22]/50 border-[#27272A]/50 focus:border-[#00E599] focus:ring-1 focus:ring-[#00E599]/50 h-12 rounded-lg" data-testid="contact-company-input" />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-2">Project Details <span className="text-[#00E599]">*</span></label>
                <Textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your project, goals, and timeline..." rows={5} className="bg-[#1F1F22]/50 border-[#27272A]/50 focus:border-[#00E599] focus:ring-1 focus:ring-[#00E599]/50 rounded-lg resize-none" data-testid="contact-message-input" />
              </div>
              <Button type="submit" disabled={isSubmitting} className="w-full bg-[#00E599] text-black hover:bg-[#00E599]/90 h-12 rounded-lg font-medium transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50" data-testid="contact-submit-button">
                {isSubmitting ? "Sending..." : (<>Send Message<Send className="ml-2 w-4 h-4" /></>)}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();
  return (
    <footer className="py-8 border-t border-[#27272A]/40">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="font-heading font-bold text-lg tracking-tight"><span className="text-[#00E599]">COOKIE</span>PHANTOM</div>
          <p className="text-sm text-[#A1A1AA]">{currentYear} CookiePhantom AI Farm. Engineering practical solutions.</p>
          <div className="flex items-center gap-6">
            <a href="#services" className="text-sm text-[#A1A1AA] hover:text-white transition-colors">Services</a>
            <a href="#contact" className="text-sm text-[#A1A1AA] hover:text-white transition-colors">Contact</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page
const LandingPage = () => (
  <div className="min-h-screen bg-[#050505] text-[#EDEDED]">
    <Navbar />
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}><Hero /></motion.div>
    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.8 }}><Services /></motion.div>
    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.8 }}><Process /></motion.div>
    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.8 }}><TechStack /></motion.div>
    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.8 }}><WhyChooseUs /></motion.div>
    <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.8 }}><Contact /></motion.div>
    <Footer />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" />
    </div>
  );
}

export default App;
